import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Download, FileSpreadsheet } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { exportEntities, fetchEntityReport } from "../../api/admin";

const Bar = ({ value }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary/15">
    <div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${value}%` }} />
  </div>
);

export const EntityReport = ({ filters }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchEntityReport({ from_date: filters.from_date, to_date: filters.to_date }).then(setData);
  }, [filters.from_date, filters.to_date]);

  if (!data) return null;

  return (
    <NeuCard data-testid="entity-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" /> {t("reports.entitiesTitle")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-mono text-xs text-muted-foreground" data-testid="entity-totals">
            {data.totals.entities} · {data.totals.headcount} · {data.totals.worked_hours}h
          </p>
          <NeuButton
            data-testid="entity-export-excel"
            className="flex items-center gap-2 px-3 py-2 text-xs"
            onClick={() => exportEntities("xlsx", i18n.language, filters)}
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </NeuButton>
          <NeuButton
            data-testid="entity-export-pdf"
            className="flex items-center gap-2 px-3 py-2 text-xs"
            onClick={() => exportEntities("pdf", i18n.language, filters)}
          >
            <Download className="h-4 w-4" /> PDF
          </NeuButton>
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        {data.entities.map((e) => (
          <div
            key={e.branch_id}
            data-testid={`entity-row-${e.branch_id}`}
            className="rounded-2xl border border-border/50 px-4 py-3 shadow-neu-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{e.name}</p>
                <p className="text-[10px] font-bold text-primary">{t(`branches.entity.${e.entity_type}`)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-4 font-mono text-xs">
                <span title={t("reports.headcount")}>{e.headcount} 👤</span>
                <span title={t("reports.hours")}>{e.worked_hours}h</span>
                <span className={e.late_records ? "text-destructive" : "text-success"}>
                  {e.punctuality_rate}%
                </span>
                <span className="font-bold text-primary">{e.attendance_rate}%</span>
              </div>
            </div>
            <div className="mt-3">
              <Bar value={e.attendance_rate} />
            </div>
          </div>
        ))}
      </div>
    </NeuCard>
  );
};
